<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { authStore } from '@/stores/auth.js'

const route = useRoute()
const router = useRouter()
const tenant = computed(() => authStore.state.selectedTenant)
const project = computed(() => authStore.state.assignment?.project)
const mobileNavigationOpen = ref(false)
const mobileMenuButton = ref(null)
const mobileDrawer = ref(null)
let removeNavigationGuard = null
let previousBodyOverflow = ''

const projectBase = computed(() => `/project-manager/projects/${encodeURIComponent(route.params.projectId || project.value?.id || '')}`)
const navigation = computed(() => [
  { to: `${projectBase.value}/dashboard`, icon: 'A', label: '项目大脑', description: '策略与竞品研究' },
  {
    icon: 'E', label: '企业基本资料', description: '企业画像与市场分析', children: [
      { to: `${projectBase.value}/company/basic`, label: '企业基本信息', description: '产品、亮点、地区' },
      { to: `${projectBase.value}/company/market`, label: '企业市场分析', description: 'AI 用户洞察与内容策略' },
    ],
  },
  { to: `${projectBase.value}/materials`, icon: 'B', label: '企业真实素材库', description: '素材包与缺口' },
  { to: `${projectBase.value}/personas`, icon: 'C', label: '素人20人设矩阵', description: '二维码分享与领取' },
  { to: `${projectBase.value}/user-apps`, icon: 'U', label: '我的用户 App', description: '姓名、手机号与专属代码' },
  { to: `${projectBase.value}/daily-tasks`, icon: 'D', label: '每日任务中心', description: '下发到手机助手' },
])
const activeNavigation = computed(() => {
  for (const item of navigation.value) {
    if (item.to && isActive(item.to)) return item
    const child = item.children?.find((candidate) => isActive(candidate.to))
    if (child) return child
  }
  return { label: route.meta.title || '项目工作台' }
})

function isActive(path) {
  return route.path === path || route.path.startsWith(`${path}/`)
}

async function openMobileNavigation() {
  mobileNavigationOpen.value = true
  await nextTick()
  mobileDrawer.value?.querySelector('a.active, a')?.focus()
}

function closeMobileNavigation({ restoreFocus = true } = {}) {
  if (!mobileNavigationOpen.value) return
  mobileNavigationOpen.value = false
  if (restoreFocus) nextTick(() => mobileMenuButton.value?.focus())
}

function handleDrawerKeydown(event) {
  if (event.key === 'Escape') {
    event.preventDefault()
    closeMobileNavigation()
    return
  }
  if (event.key !== 'Tab') return
  const focusable = [...(mobileDrawer.value?.querySelectorAll('a[href], button:not([disabled])') || [])]
  if (!focusable.length) return
  const first = focusable[0]
  const last = focusable.at(-1)
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(() => route.fullPath, () => closeMobileNavigation({ restoreFocus: false }))
watch(mobileNavigationOpen, (open) => {
  if (typeof document === 'undefined') return
  if (open) {
    previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = previousBodyOverflow
  }
})

onMounted(() => {
  removeNavigationGuard = router.beforeEach(() => {
    if (!mobileNavigationOpen.value) return true
    closeMobileNavigation()
    return false
  })
})
onBeforeUnmount(() => {
  removeNavigationGuard?.()
  if (typeof document !== 'undefined') document.body.style.overflow = previousBodyOverflow
})

async function logout() {
  await authStore.logout()
  await router.replace('/login')
}
</script>

<template>
  <div class="project-workspace-shell">
    <button ref="mobileMenuButton" class="project-mobile-menu" type="button" :aria-expanded="mobileNavigationOpen" aria-controls="project-navigation" @click="mobileNavigationOpen ? closeMobileNavigation() : openMobileNavigation()">
      <span aria-hidden="true">PM</span><strong>{{ activeNavigation.label }}</strong><i aria-hidden="true">☰</i>
    </button>
    <button v-if="mobileNavigationOpen" class="project-nav-backdrop" type="button" aria-label="关闭导航" @click="closeMobileNavigation()"></button>
    <aside id="project-navigation" ref="mobileDrawer" class="project-sidebar" :class="{ open: mobileNavigationOpen }" @keydown="handleDrawerKeydown">
      <div class="project-brand">
        <span class="project-brand-mark">PM</span>
        <span><strong>AI 营销项目经理</strong><small>项目执行工作台</small></span>
      </div>
      <nav class="project-nav" aria-label="AI营销项目经理主导航">
        <template v-for="item in navigation" :key="item.to || item.label">
          <section v-if="item.children" class="project-nav-group" :class="{ active: item.children.some((child) => isActive(child.to)) }">
            <div class="project-nav-item project-nav-heading">
              <span class="project-nav-icon">{{ item.icon }}</span>
              <span><strong>{{ item.label }}</strong><small>{{ item.description }}</small></span>
            </div>
            <router-link v-for="child in item.children" :key="child.to" :to="child.to" class="project-nav-child" :class="{ active: isActive(child.to) }" :aria-current="isActive(child.to) ? 'page' : undefined" @click="closeMobileNavigation({ restoreFocus: false })">
              <strong>{{ child.label }}</strong><small>{{ child.description }}</small>
            </router-link>
          </section>
          <router-link v-else :to="item.to" class="project-nav-item" :class="{ active: isActive(item.to) }" :aria-current="isActive(item.to) ? 'page' : undefined" @click="closeMobileNavigation({ restoreFocus: false })">
            <span class="project-nav-icon">{{ item.icon }}</span>
            <span><strong>{{ item.label }}</strong><small>{{ item.description }}</small></span>
          </router-link>
        </template>
      </nav>
      <footer class="project-sidebar-footer">
        <span><small>企业空间</small><strong>{{ tenant?.name }}</strong></span>
        <button type="button" @click="logout">退出登录</button>
      </footer>
    </aside>

    <section class="project-workspace-main">
      <header class="project-workspace-topbar">
        <div><span class="topbar-level">AI 营销项目经理</span><i aria-hidden="true"></i><strong>{{ route.meta.title }}</strong></div>
        <p><span>当前项目</span><strong>{{ project?.name }}</strong></p>
      </header>
      <main class="project-workspace-content">
        <RouterView :key="`${tenant?.id}:${project?.id}`" v-slot="{ Component }">
          <KeepAlive :max="6">
            <component :is="Component" />
          </KeepAlive>
        </RouterView>
      </main>
    </section>
  </div>
</template>

<style scoped>
.project-workspace-shell { min-height: 100vh; display: grid; grid-template-columns: 310px minmax(0, 1fr); background: #f5f8fa; }
.project-mobile-menu,.project-nav-backdrop { display: none; }
.project-sidebar { position: sticky; top: 0; height: 100vh; display: flex; flex-direction: column; overflow: hidden; border-right: 1px solid #dfe8ec; background: #fff; }
.project-brand { display: grid; grid-template-columns: 50px minmax(0, 1fr); gap: 16px; align-items: center; padding: 24px 22px; border-bottom: 1px solid #e5ecef; }
.project-brand-mark { display: grid; place-items: center; width: 50px; height: 50px; border-radius: 12px; color: #08746d; background: #dff3f1; font-weight: 900; }
.project-brand strong, .project-brand small, .project-nav-item strong, .project-nav-item small, .project-nav-child strong, .project-nav-child small { display: block; }
.project-brand strong { color: #17313b; font-size: 18px; }
.project-brand small { margin-top: 4px; color: #70858d; font-size: 13px; }
.project-brand .project-level { width: fit-content; margin: 0 0 5px; padding: 3px 7px; border-radius: 999px; color: #08746d; background: #e2f5f2; font-size: 10px; font-weight: 900; }
.project-nav { flex: 1; min-height: 0; display: grid; align-content: start; gap: 8px; padding: 18px 14px; overflow: auto; }
.project-nav-item { display: grid; grid-template-columns: 38px minmax(0, 1fr); gap: 11px; align-items: center; min-height: 66px; padding: 10px 12px; border-radius: 12px; color: #4c626b; text-decoration: none; }
.project-nav-item:hover, .project-nav-item.active, .project-nav-group.active { color: #17313b; background: #eaf7f5; }
.project-nav-icon { display: grid; place-items: center; width: 38px; height: 38px; border-radius: 10px; color: #34515a; background: #edf1f3; font-weight: 900; }
.project-nav-item strong { font-size: 15px; }.project-nav-item small { margin-top: 3px; color: #81939a; font-size: 12px; }
.project-nav-group { border-radius: 12px; padding-bottom: 6px; }.project-nav-heading { background: transparent !important; }
.project-nav-child { display: grid; gap: 2px; margin: 0 10px 6px 61px; padding: 9px 11px; border-radius: 9px; color: #5b7179; text-decoration: none; }
.project-nav-child:hover, .project-nav-child.active { color: #17313b; background: rgba(21, 152, 142, .12); }
.project-nav-child strong { font-size: 14px; }.project-nav-child small { color: #82949b; font-size: 11px; }
.project-sidebar-footer { display: grid; gap: 12px; padding: 18px 20px; border-top: 1px solid #e5ecef; }
.project-sidebar-footer span { min-width: 0; }.project-sidebar-footer small, .project-sidebar-footer strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.project-sidebar-footer small { color: #7a8e95; }.project-sidebar-footer strong { margin-top: 3px; color: #263f47; font-size: 13px; }
.project-sidebar-footer button { justify-self: start; padding: 0; border: 0; color: #08746d; background: none; font-weight: 800; cursor: pointer; }
.project-workspace-main { min-width: 0; }.project-workspace-topbar { min-height: 82px; display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 16px 32px; border-bottom: 1px solid #e0e8ec; background: rgba(255,255,255,.92); }
.project-workspace-topbar > div { display: flex; align-items: center; gap: 14px; color: #0c9188; }.project-workspace-topbar i { width: 1px; height: 22px; background: #d7e2e5; }.project-workspace-topbar strong { color: #273d46; }
.project-workspace-topbar .topbar-level { padding: 6px 10px; border-radius: 999px; color: #08746d; background: #e2f5f2; font-size: 12px; font-weight: 900; }
.project-workspace-topbar p { margin: 0; text-align: right; }.project-workspace-topbar p span, .project-workspace-topbar p strong { display: block; }.project-workspace-topbar p span { color: #82949b; font-size: 11px; }.project-workspace-topbar p strong { margin-top: 3px; font-size: 13px; }
.project-workspace-content { width: min(1500px, 100%); margin: 0 auto; padding: 30px; }
@media (max-width: 900px) {
  .project-workspace-shell { display: block; padding-top: 62px; }
  .project-mobile-menu { position: fixed; z-index: 80; top: 0; right: 0; left: 0; display: grid; grid-template-columns: 40px minmax(0,1fr) 32px; align-items: center; gap: 10px; min-height: 62px; padding: 8px 14px; border: 0; border-bottom: 1px solid #c9dddd; color: #fff; background: #124f52; text-align: left; box-shadow: 0 6px 18px rgba(19,49,57,.16); }
  .project-mobile-menu>span { display: grid; place-items: center; width: 40px; height: 40px; border-radius: 10px; color: #124f52; background: #dff3f1; font-weight: 900; }
  .project-mobile-menu>strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .project-mobile-menu>i { font-size: 24px; font-style: normal; text-align: center; }
  .project-nav-backdrop { position: fixed; z-index: 84; inset: 0; display: block; border: 0; background: rgba(17,35,42,.56); }
  .project-sidebar { position: fixed; z-index: 85; top: 0; bottom: 0; left: 0; width: min(330px,88vw); height: 100vh; transform: translateX(-105%); transition: transform .2s ease; box-shadow: 22px 0 55px rgba(18,43,50,.25); }
  .project-sidebar.open { transform: translateX(0); }
  .project-brand { padding: 18px 16px; }
  .project-nav { display: grid; gap: 8px; padding: 14px 12px; overflow: auto; }
  .project-nav-item { min-height: 58px; }
  .project-nav-child { margin-left: 52px; }
  .project-sidebar-footer { display: grid; }
  .project-workspace-topbar { display: none; }
  .project-workspace-content { padding: 18px; }
}
</style>
